import ChartMetric from '@/Enums/Chart/Metric.enum';
import InsightCallout from '@/components/InsightCallout';
import { getVerticalFootTrafficGraphData } from '@/helpers/attribution/chartData';
import { selectFootTrafficDwellInsight } from '@/helpers/attribution/footTrafficInsights';
import { downloadCSV } from '@/helpers/downloadCSV';
import { useAppSelector } from '@/store/hooks';
import { selectFootTrafficDwellReport } from '@/store/selectors/attribution/footTraffic.selectors';
import { Box, Typography } from '@mui/material';
import { useCallback, useMemo } from 'react';
import VerticalChart from '../Charts/VerticalChart';
import GraphCard from './BaseCard/GraphCard';

const label = 'Dwell Time';

const DwellTimeCard = () => {
  const { loading, dwellReport } = useAppSelector(selectFootTrafficDwellReport);
  const { text: insightText } = useAppSelector(selectFootTrafficDwellInsight);
  const { segments, avgDwellTime, medianDwellTime } = dwellReport;

  const { chartData, csvData } = useMemo(() => getVerticalFootTrafficGraphData(segments, loading), [segments, loading]);

  const noData = useMemo<boolean>(() => Object.values(segments).every(visits => !visits), [segments]);

  const topSegment = useMemo(
    () =>
      Object.entries(segments).reduce<[string, number] | null>(
        (top, entry) => (!top || entry[1] > top[1] ? entry : top),
        null,
      )?.[0],
    [segments],
  );

  const onDownload = useCallback(() => {
    if (!loading) {
      downloadCSV(csvData, label);
    }
  }, [csvData, loading]);

  return (
    <GraphCard label={label} loading={loading} noData={noData} onDownload={onDownload} allowPrint>
      <Box sx={{ display: 'flex', gap: '30px', mb: 2 }}>
        <Box>
          <Typography sx={{ color: 'text.secondary', fontSize: 13 }}>Avg Dwell Time</Typography>
          <Typography sx={{ fontSize: 20, fontWeight: 600 }}>{avgDwellTime} min</Typography>
        </Box>
        <Box>
          <Typography sx={{ color: 'text.secondary', fontSize: 13 }}>Median Dwell Time</Typography>
          <Typography sx={{ fontSize: 20, fontWeight: 600 }}>{medianDwellTime} min</Typography>
        </Box>
      </Box>
      {!noData && topSegment && (
        <Typography sx={{ color: 'text.secondary', fontSize: 13, mb: 1 }}>Most visitors stay {topSegment}</Typography>
      )}
      <VerticalChart chartData={chartData} legendLabel={ChartMetric.FootTrafficVisits} />
      <InsightCallout text={insightText} loading={loading} />
    </GraphCard>
  );
};

export default DwellTimeCard;
